/**
 * Connection Lost Domain
 * Manages state for unexpected disconnection notifications and silent reconnect attempts.
 * The socket service (nakama-service.ts) says what happens to the connection
 * through the handlers below — a drop, the reconnect over, a ban barring it —
 * and they write the stores, this one and what a drop ends: the service
 * writes none, itself or through a helper (design-checks'
 * store-written-outside-domain holds the socket layer to that).
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { endPlay } from './end-play';
import { handleGameSocketDropped } from './voice-chat';
import { resubscribeAirportFlightsAfterRejoin } from './itineraries';

// ==================== TYPES ====================

interface ConnectionLostState {
  /** True while the reconnect service is actively retrying */
  isReconnecting: boolean;
  /**
   * True once every reconnect attempt has failed: the Connection lost notice is
   * open (domains/notices.ts draws one notice at a time).
   */
  noticeOpen: boolean;
  /**
   * True once the server has ended this player's session for good: a ban,
   * whose notice arrives just before the server closes the socket
   * (banned-players.go). From then on a dropped socket is not reconnected,
   * and neither the reconnecting banner nor Connection lost is shown, whoever
   * asks for them: the ban notice is the one thing on screen until the
   * player acts on it. It lasts as long as the session — until the page
   * reloads, as the notice's OK does, or the game's stores are reset as the
   * player leaves it — so a later session in the tab reconnects as usual.
   */
  reconnectBarred: boolean;
  setIsReconnecting: (reconnecting: boolean) => void;
  setNoticeOpen: (open: boolean) => void;
  /**
   * Bars a reconnect from now on (see reconnectBarred). Ending a session's
   * reconnecting is nakamaService.stopReconnecting, which bars it through
   * handleReconnectBarred and stops a reconnect already under way too: call
   * that.
   */
  barReconnect: () => void;
  /** Back to how a session starts, as the game's stores are reset. */
  reset: () => void;
}

// ==================== STORE ====================

export const useConnectionLostStore = create<ConnectionLostState>()(
  devtools(
    (set) => ({
      isReconnecting: false,
      noticeOpen: false,
      reconnectBarred: false,

      setIsReconnecting: (reconnecting: boolean) => {
        set((state) => ({ isReconnecting: reconnecting && !state.reconnectBarred }), false, 'setIsReconnecting');
      },

      setNoticeOpen: (open: boolean) => {
        set((state) => ({ noticeOpen: open && !state.reconnectBarred }), false, 'setNoticeOpen');
      },

      barReconnect: () => {
        set({ reconnectBarred: true, isReconnecting: false, noticeOpen: false }, false, 'barReconnect');
      },

      reset: () => {
        set({ reconnectBarred: false, isReconnecting: false, noticeOpen: false }, false, 'reset');
      },
    }),
    {
      name: 'connection-lost-store',
    }
  )
);

// ==================== HANDLERS ====================

/** Whether a drop may start a reconnect: not once a ban has ended the session (reconnectBarred). */
export function reconnectAllowed(): boolean {
  return !useConnectionLostStore.getState().reconnectBarred;
}

/**
 * The socket dropped, or a drop is simulated (nakama-service.ts). What it ends
 * goes at once — voice chat, and what play had under way (endPlay: a
 * moderator's call, the command on its way) — while the game screen stays for
 * the reconnecting banner, which goes up over it unless a ban barred
 * reconnecting, whose notice stays the one on screen. A drop while a
 * reconnect is under way finds all of this done already.
 */
export function handleDropped(): void {
  handleGameSocketDropped();
  endPlay();
  useConnectionLostStore.getState().setIsReconnecting(true);
}

/**
 * The reconnect is over, back in the match or failed at every attempt: the
 * banner goes. A failure ends play until the player acts, and Connection lost
 * opens — unless a ban barred it, whose own notice stays the one on screen.
 * What play had under way ended with the drop already; endPlay says so for
 * this ending as for the others. A reconnect stopped — the player leaving, a
 * ban — is said by neither. Back in the match, the open airport's flights are
 * asked for again, as the server may have dropped the subscription.
 */
export function handleReconnectOver(failed: boolean): void {
  useConnectionLostStore.getState().setIsReconnecting(false);
  if (!failed) {
    resubscribeAirportFlightsAfterRejoin();
    return;
  }
  endPlay();
  useConnectionLostStore.getState().setNoticeOpen(true);
}

/**
 * A ban ended the session: no drop reconnects from now on, and the banner and
 * Connection lost stay shut (reconnectBarred). Through
 * nakamaService.stopReconnecting, which stops a reconnect under way too.
 */
export function handleReconnectBarred(): void {
  useConnectionLostStore.getState().barReconnect();
}
